<template>
  <div class="container p-0 mt-3 mt-md-5">

    <div class="page-header gold mb-3">
      <back-btn :to="$route.query.from || '/dashboard'"></back-btn>
      <h2 class="mb-0">
        <span v-text="this.$route.meta.title"></span>
      </h2>
    </div>

    <div class="card">
      <div class="row g-0">
        <Recipients
          :conversations="conversations"
          :selectedRecipient="selectedRecipient"
        ></Recipients>

        <div class="col-12 col-lg-8 col-xl-9 d-flex flex-column messages-container" :class="{ 'd-none d-lg-flex': !selectedRecipient }">
          <div class="py-2 px-4 border-bottom d-none d-lg-block" v-if="selectedRecipient">
            <div class="d-flex align-items-center py-1">
              <div class="position-relative">
                <img :src="selectedRecipient.profile_image || '/profile_images/default.png'" class="rounded-circle mr-1" :alt="selectedRecipient.first_name" width="40" height="40" />
              </div>
              <div class="flex-grow-1 pl-3">
                <router-link
                  :to="{ path: '/member/' + selectedRecipient.id, query: { from: $route.fullPath } }"
                  class="recipient-name-link"
                  v-b-tooltip.hover
                  title="View Profile"
                >
                  {{ selectedRecipient.first_name }} {{ selectedRecipient.last_name }}
                </router-link>
                <div class="text-muted small" v-if="selectedRecipient.summary && selectedRecipient.summary !== 'null'">
                  <em>{{ selectedRecipient.summary }}</em>
                </div>
              </div>
            </div>
          </div>
          <div class="position-relative flex-grow-1" ref="chatbox" style="overflow-y: auto">
            <div class="d-flex justify-content-center mt-5" v-if="loading.messages">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else>
              <div class="pt-3 text-center text-black-50" style="font-style: italic" v-if="! (messages && messages.length)">
                This conversation is empty
              </div>
            </div>
            <Messages
              :messages="messages"
              :hasMore="hasMore"
              :coach="selectedRecipient"
              @loadMore="getMessages(true)"
            ></Messages>
          </div>
          <div class="flex-grow-0 py-2 px-2 input-box">
            <div class="position-relative">
              <textarea ref="messageInput" v-model="message" @keydown.enter.exact.prevent="sendMessage" class="form-control shadow-none" placeholder="Type your message (Shift+Enter for new line)" rows="3" />
              <button
                class="btn btn-link p-0 position-absolute text-primary shadow-none"
                @click="sendMessage()"
              >
                <i class="fa fa-paper-plane fa-lg gold"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
import Messages from "@/components/Chatbox/Messages";
import Recipients from "@/components/Chatbox/Recipients";
import BackBtn from "@/components/BackBtn.vue";

export default {
  components: {
    BackBtn,
    Recipients,
    Messages
  },
  watch:{
    $route () {
      this.selectRecipient()
    }
  },
  computed: {
    profile() {
      return this.$store.state.profile;
    },
  },
  created: function() {
    this.getMembers()
  },
  data() {
    return {
      selectedRecipient: null,
      coaches: [],
      connections: [],
      conversations: [],
      messages: [],
      message: '',
      loading: {messages: false},
      hasMore: false,
    }
  },
  methods: {

    sendMessage() {

      if (! this.message || ! this.selectedRecipient) {
        return
      }

      const message = this.message
      this.message = ''

      this.messages.push({
        message,
        sender_id: this.profile.id,
        receiver_id: this.selectedRecipient.id,
        id: Date.now(),
        created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      })
      this.$nextTick(() => {
        this.$refs['chatbox'].scrollTop = this.$refs['chatbox'].scrollHeight
        this.$refs['messageInput'].focus()
      })

      this.loading.sendMessage = true

      axios.post('sendMessage', { recipient_id: this.selectedRecipient.id, message: message })
        .then(() => {
          this.loading.sendMessage = false
        })
    },

    getMembers() {
      const selectedUserId = this.$route.params.recipientId

      axios(`getChatUsers?userId=${selectedUserId}`)
        .then((response) => {
          this.conversations = response.data.conversations ?? [];

          if (this.$route.params.recipientId) {
            this.selectRecipient(response.data.preferredRecipient)
            return
          }

          const defaultRecipient = this.getRecipient()
          if (defaultRecipient) {
            this.$router.push(`/chatbox/${defaultRecipient.id}`)
          }
        })
    },

    selectRecipient(preferredRecipient) {
      this.selectedRecipient = this.getRecipient(this.$route.params.recipientId)

      if (! this.selectedRecipient && preferredRecipient) {
        this.connections.push(preferredRecipient)
        this.selectedRecipient = preferredRecipient
      }

      if (! this.selectedRecipient && this.$route.params.recipientId) {
        this.fetchRecipientById(this.$route.params.recipientId)
        return
      }

      if (this.selectedRecipient) {
        this.messages = []
        this.getMessages()
      }
    },

    fetchRecipientById(userId) {
      const params = new URLSearchParams()
      params.append('user_id', userId)

      axios({ url: 'userPublicProfile', params })
        .then((response) => {
          const user = response.data && response.data.profile
          if (user) {
            const alreadyListed = this.conversations.find((u) => u.id == user.id)
            if (!alreadyListed) {
              this.conversations.push(user)
            }
            this.selectedRecipient = user
            this.messages = []
            this.getMessages()
          }
        })
        .catch((err) => console.error('Failed to load recipient profile', err))
    },

    getMessages(loadMore) {
      const recipientId = this.selectedRecipient.id

      let params = `recipientId=${recipientId}`

      if (loadMore) {
        const firstMsgId = this.messages.find(() => true)?.id
        params += `&cursor=${firstMsgId}`
      }

      const url = `getMessages?${params}`;

      this.loading.messages = true

      axios(url)
        .then((response) => {
          let messages = response.data.messages || []
          this.hasMore = messages.length === response.data.limit

          // messages = messages.slice().reverse()

          if (loadMore) {
            this.messages = messages.concat(this.messages)
          } else {
            this.messages = messages
          }
          this.loading.messages = false

          if (! loadMore) {
            this.$nextTick(() => {
              this.$refs['chatbox'].scrollTop = this.$refs['chatbox'].scrollHeight
            })
          }
        })
    },

    getRecipient(recipientId) {
      return this.connections.concat(this.coaches)
        .find(({id}) => recipientId === undefined || id == recipientId)
    }
  }
}

</script>

<style scoped>
.input-box {
  textarea {
    padding-right: 36px;
    &::placeholder {
      color: #999;
      font-size: 12px;
    }
  }

  button {
    bottom: 10px;
    right: 10px;
  }
}

.recipient-name-link {
  color: inherit;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;
}

.recipient-name-link:hover {
  color: #d6b674;
  text-decoration: none;
}

.pending-banner {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 40px 24px;
  background: rgba(224, 163, 32, 0.04);
}

.pending-banner__avatar img {
  border: 3px solid rgba(224, 163, 32, 0.4);
}

.pending-banner__text {
  font-size: 0.95rem;
  color: #555;
  text-align: center;
  margin: 0;
}

.pending-banner__actions {
  display: flex;
  gap: 10px;
}

.btn-accept-conn,
.btn-decline-conn {
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  border-radius: 20px;
  padding: 7px 20px;
  cursor: pointer;
  transition: opacity 0.15s;
}

.btn-accept-conn:disabled,
.btn-decline-conn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-accept-conn {
  background: #28a745;
  color: #fff;
}

.btn-accept-conn:hover:not(:disabled) {
  opacity: 0.85;
}

.btn-decline-conn {
  background: #dc3545;
  color: #fff;
}

.btn-decline-conn:hover:not(:disabled) {
  opacity: 0.85;
}

.messages-container {
  height: calc(100vh - 150px);
}

@media screen and (min-width: 768px) {
  .messages-container {
    height: calc(100vh - 190px);
  }
}

@media screen and (min-width: 992px) {
  .messages-container {
    height: calc(100vh - 120px);
  }
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  position: relative;
}
</style>
